import { isObject } from '@scalar/helpers/object/is-object';
import { getResolvedRef } from '@scalar/workspace-store/helpers/get-resolved-ref';
import { unpackProxyObject } from '@scalar/workspace-store/helpers/unpack-proxy';
/**
 * Recursively resolves all $ref objects in a data structure to their actual values.
 * Traverses through objects, arrays, and nested structures to find and resolve
 * any $ref references at any depth level.
 *
 * Handles circular references gracefully by detecting them and returning '[circular]'
 * to prevent infinite loops.
 */
export const getResolvedRefDeep = (node) => {
    const visited = new WeakSet();
    const cachedResults = new WeakMap();
    const resolveNode = (current) => {
        // Only recurse into objects and arrays; return primitives as-is
        if (!isObject(current) && !Array.isArray(current)) {
            return current;
        }
        const rawValue = unpackProxyObject(current, { depth: 1 });
        // We don't have to recurse into the same object again
        // This helps us having to manually remove the tracked node after we recurse into the tree
        if (cachedResults.has(rawValue)) {
            return cachedResults.get(rawValue);
        }
        // Break circular references
        if (visited.has(rawValue)) {
            return '[circular]';
        }
        // Track visited nodes
        visited.add(rawValue);
        if ('$ref' in current) {
            const resolved = getResolvedRef(current);
            const result = resolveNode(resolved);
            // Preserve keywords declared alongside the `$ref`. A `$ref` may carry siblings that specialize the
            // target — most notably the `$defs`/`$dynamicAnchor` binding used to express a generic schema like
            // `Paginated<Planet>`. Per OpenAPI 3.1, siblings of a `$ref` override the resolved value, so we merge
            // them on top. Without this the dynamic-ref binding is dropped and `$dynamicRef` cannot resolve.
            const siblings = Object.entries(current).filter(([key]) => key !== '$ref' && key !== '$ref-value');
            if (siblings.length > 0 && isObject(result)) {
                const merged = { ...result };
                for (const [key, value] of siblings) {
                    merged[key] = resolveNode(value);
                }
                cachedResults.set(rawValue, merged);
                return merged;
            }
            cachedResults.set(rawValue, result);
            return result;
        }
        // For arrays
        if (Array.isArray(current)) {
            const result = current.map(resolveNode);
            cachedResults.set(rawValue, result);
            return result;
        }
        const result = Object.fromEntries(Object.entries(current).map(([key, value]) => [key, resolveNode(value)]));
        cachedResults.set(rawValue, result);
        return result;
    };
    return resolveNode(node);
};
